import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { OnScreenModal } from "@/components/ui/OnScreenModal";
import { generateChargebackPacket } from "@/lib/api/paymentLogService";
import { showError, showSuccess } from "@/lib/utils/notifications";
import { PaymentAdjustmentModal } from "../modals/PaymentAdjustmentModal";
import { ExternalPaymentModal } from "../modals/ExternalPaymentModals";
import { ApplicationAgreementsSummary } from "../ApplicationAgreementsSummary";
import styles from "../../styles/applicationDetail.module.css";
import modalStyles from "../../styles/modals.module.css";
import type {
  PaymentAdjustmentApplicationContext,
  PaymentAdjustmentHistoryLine,
} from "../../types/modals";
import type { SignaturePaymentModalsProps } from "../../types";

export function SignaturePaymentModals({ tab }: SignaturePaymentModalsProps) {
  const {
    applicationAllData,
    applicantId,
    showAgreementsModal,
    showExternalPayment,
    externalPaymentType,
    showAdjustmentModal,
    selectedPaymentLine,
    clearSignatureConfirmOpen,
    paymentDeleteConfirm,
    setShowAgreementsModal,
    setShowExternalPayment,
    setShowAdjustmentModal,
    setClearSignatureConfirmOpen,
    setPaymentDeleteConfirm,
    performClearSignature,
    performDeletePayment,
    handlePaymentAdjustmentSubmit,
    refreshAfterAdminAction,
  } = tab;

  return (
    <>
      {showAdjustmentModal && (
        <PaymentAdjustmentModal
          isOpen={showAdjustmentModal}
          onClose={() => setShowAdjustmentModal(false)}
          payment={selectedPaymentLine as PaymentAdjustmentHistoryLine | null}
          application={applicationAllData as PaymentAdjustmentApplicationContext}
          onSubmit={async (data) => {
            try {
              await handlePaymentAdjustmentSubmit(data);
            } catch (err: unknown) {
              showError(
                err instanceof Error ? err.message : "Failed to process adjustment",
              );
            }
          }}
          onGenerateChargebackPacket={async ({ applicantId, paymentLogId }) => {
            try {
              await generateChargebackPacket(applicantId, paymentLogId);
              showSuccess("Chargeback Packet Generated Sucessfully!");
              setShowAdjustmentModal(false);
              await refreshAfterAdminAction();
            } catch (err: unknown) {
              showError(
                err instanceof Error
                  ? err.message
                  : "Failed to generate chargeback packet",
              );
            }
          }}
        />
      )}

      <ExternalPaymentModal
        isOpen={showExternalPayment}
        onClose={() => setShowExternalPayment(false)}
        applicantId={applicantId}
        applicationAllData={applicationAllData}
        paymentType={externalPaymentType}
        onSuccess={() => {
          setShowExternalPayment(false);
          void refreshAfterAdminAction();
        }}
      />

      <OnScreenModal
        isOpen={showAgreementsModal}
        title="Agreements Section"
        onClose={() => setShowAgreementsModal(false)}
        maxWidth="max-w-3xl"
        headerVariant="centeredTitle"
        bodyScrollOnly
        overlayDataTestId="application-signature-payment-tab-agreements-modal"
        panelDataTestId="application-signature-payment-tab-agreements-modal-panel"
        titleDataTestId="application-signature-payment-tab-agreements-modal-title"
        closeButtonDataTestId="application-signature-payment-tab-agreements-modal-close-button"
        bodyClassName={styles.agreementsModalBody}
        footer={
          <div className={modalStyles.modalFooterActionGroup}>
            <button
              type="button"
              className={modalStyles.modalActionPrimary}
              onClick={() => setShowAgreementsModal(false)}
              data-testid="application-signature-payment-tab-agreements-modal-close-footer-button"
            >
              OK
            </button>
          </div>
        }
      >
        <ApplicationAgreementsSummary applicantId={applicantId} />
      </OnScreenModal>

      <ConfirmDialog
        isOpen={clearSignatureConfirmOpen}
        title="Clear Signature"
        message="Do you want to Remove Signature Info ?"
        primaryLabel="Yes"
        onPrimary={() => void performClearSignature()}
        secondaryLabel="No"
        onSecondary={() => setClearSignatureConfirmOpen(false)}
        onDismiss={() => setClearSignatureConfirmOpen(false)}
        overlayDataTestId="application-signature-payment-tab-confirm-clear-signature"
        primaryButtonDataTestId="application-signature-payment-tab-confirm-clear-signature-primary-button"
        secondaryButtonDataTestId="application-signature-payment-tab-confirm-clear-signature-secondary-button"
        closeButtonDataTestId="application-signature-payment-tab-confirm-clear-signature-close-button"
      />

      <ConfirmDialog
        isOpen={paymentDeleteConfirm.open}
        title="Delete Payment Log"
        message="Are you sure you want to delete this payment log?"
        primaryLabel="Delete"
        onPrimary={() => void performDeletePayment()}
        secondaryLabel="Cancel"
        onSecondary={() =>
          setPaymentDeleteConfirm({ open: false, payment: null })
        }
        variant="warning"
        presentation="saveChanges"
        overlayDataTestId="application-signature-payment-tab-confirm-delete-payment-log"
        primaryButtonDataTestId="application-signature-payment-tab-confirm-delete-payment-log-primary-button"
        secondaryButtonDataTestId="application-signature-payment-tab-confirm-delete-payment-log-secondary-button"
        closeButtonDataTestId="application-signature-payment-tab-confirm-delete-payment-log-close-button"
      />
    </>
  );
}
